import axios from 'axios'
import { Platform } from 'quasar'
import {
  LS_CAPACITOR_SUBDOMAIN,
  normalizeShellSubdomain,
  isValidShellSubdomain
} from '@/config/electronShellStorage'

const PRIMARY_SUFFIX = String(import.meta.env.API_DOMAIN_SUFFIX || 'bluapps.dev').trim()
const RECOVERY_SUFFIX = String(import.meta.env.API_RECOVERY_DOMAIN_SUFFIX || 'bluapps-dr.dev').trim()
const RECOVERY_ENABLED = String(import.meta.env.ENABLE_API_RECOVERY || '').toLowerCase() === 'true'

const PROBE_PATH = '/health/ready'
const PROBE_TIMEOUT_MS = 8000
const FAILURE_WINDOW_MS = 45000
const FAILURE_THRESHOLD = 2
const PRIMARY_REPROBE_COOLDOWN_MS = 120000

const buildApiBase = (origin) => `${String(origin || '').replace(/\/+$/, '')}/api`

const isNativeShell = () =>
  !!Platform.is.capacitor ||
  !!Platform.is.electron ||
  (typeof window !== 'undefined' && !!window?.electronAPI?.isElectron)

function readTenantSubdomain () {
  if (typeof window === 'undefined') {
    return ''
  }
  return normalizeShellSubdomain(window.localStorage.getItem(LS_CAPACITOR_SUBDOMAIN))
}

function buildOriginForSuffix (suffix) {
  const tenant = readTenantSubdomain()
  if (!tenant || !isValidShellSubdomain(tenant)) {
    return null
  }
  return `https://${tenant}.${suffix}`
}

/** @typedef {'primary' | 'recovery'} ApiEndpointKind */

/**
 * @typedef {Object} ApiEndpointState
 * @property {ApiEndpointKind} active
 * @property {string} [reason]
 * @property {number} [switchedAt]
 * @property {ApiEndpointKind|null} [manualOverride]
 */

const state = /** @type {ApiEndpointState} */ ({
  active: 'primary',
  manualOverride: null
})

let transportFailures = []
let probeInFlight = null
let lastPrimaryProbeAt = 0

function isEnabled () {
  return RECOVERY_ENABLED && isNativeShell()
}

function getPrimaryOrigin () {
  if (import.meta.env.API_URL) {
    return String(import.meta.env.API_URL).replace(/\/+$/, '').replace(/\/api$/i, '')
  }
  return buildOriginForSuffix(PRIMARY_SUFFIX)
}

function getRecoveryOrigin () {
  return buildOriginForSuffix(RECOVERY_SUFFIX)
}

function getState () {
  return { ...state }
}

function applyActiveEndpoint (kind, reason) {
  if (state.active === kind && state.reason === reason) {
    return
  }
  state.active = kind
  state.reason = reason
  state.switchedAt = Date.now()
}

function getBaseUrl () {
  if (!isEnabled()) {
    const primary = getPrimaryOrigin()
    return primary ? buildApiBase(primary) : ''
  }

  if (state.manualOverride === 'primary' || state.manualOverride === 'recovery') {
    const origin = state.manualOverride === 'recovery' ? getRecoveryOrigin() : getPrimaryOrigin()
    return origin ? buildApiBase(origin) : ''
  }

  const origin = state.active === 'recovery' ? getRecoveryOrigin() : getPrimaryOrigin()
  return origin ? buildApiBase(origin) : ''
}

function recordTransportFailure () {
  const now = Date.now()
  transportFailures = transportFailures.filter((t) => now - t < FAILURE_WINDOW_MS)
  transportFailures.push(now)
  return transportFailures.length
}

function clearTransportFailures () {
  transportFailures = []
}

export function isTransportLayerError (error) {
  if (!error || error.isOffline) {
    return true
  }
  if (error.response) {
    return false
  }
  const code = String(error.code || '')
  if (['ECONNABORTED', 'ERR_NETWORK', 'ENOTFOUND', 'ETIMEDOUT', 'ECONNREFUSED'].includes(code)) {
    return true
  }
  return !error.response && !!error.request
}

async function probeEndpoint (kind) {
  const origin = kind === 'recovery' ? getRecoveryOrigin() : getPrimaryOrigin()
  if (!origin) {
    return false
  }

  try {
    const res = await axios.get(`${buildApiBase(origin)}${PROBE_PATH}`, {
      timeout: PROBE_TIMEOUT_MS,
      withCredentials: true,
      validateStatus: (status) => status >= 200 && status < 500
    })
    return res.status >= 200 && res.status < 400 && res.data?.status === 'ready'
  } catch {
    return false
  }
}

async function checkEndpoint (kind) {
  if (probeInFlight) {
    return probeInFlight
  }
  probeInFlight = probeEndpoint(kind).finally(() => {
    probeInFlight = null
  })
  return probeInFlight
}

async function evaluateTransportFailure (error) {
  if (!isEnabled() || !isTransportLayerError(error)) {
    return
  }

  const count = recordTransportFailure()
  if (count < FAILURE_THRESHOLD) {
    return
  }

  if (state.active === 'recovery') {
    return
  }

  const recoveryHealthy = await checkEndpoint('recovery')
  if (recoveryHealthy) {
    applyActiveEndpoint('recovery', 'network-error')
    clearTransportFailures()
  }
}

async function preferPrimaryWhenHealthy () {
  if (!isEnabled() || state.manualOverride) {
    return
  }
  if (state.active !== 'recovery') {
    return
  }

  const now = Date.now()
  if (now - lastPrimaryProbeAt < PRIMARY_REPROBE_COOLDOWN_MS) {
    return
  }
  lastPrimaryProbeAt = now

  const primaryHealthy = await checkEndpoint('primary')
  if (primaryHealthy) {
    applyActiveEndpoint('primary', 'primary-recovered')
    clearTransportFailures()
  }
}

function setManualOverride (endpoint) {
  if (endpoint !== 'primary' && endpoint !== 'recovery' && endpoint !== null) {
    throw new Error('Invalid manual endpoint override')
  }
  state.manualOverride = endpoint
  if (endpoint === 'primary' || endpoint === 'recovery') {
    applyActiveEndpoint(endpoint, 'manual-override')
  }
}

function resetForSubdomainChange () {
  state.active = 'primary'
  state.reason = undefined
  state.switchedAt = undefined
  state.manualOverride = null
  clearTransportFailures()
  lastPrimaryProbeAt = 0
}

export const apiEndpointManager = {
  isEnabled,
  getBaseUrl,
  getState,
  checkEndpoint,
  evaluateTransportFailure,
  preferPrimaryWhenHealthy,
  setManualOverride,
  resetForSubdomainChange,
  getPrimaryOrigin,
  getRecoveryOrigin
}

export default apiEndpointManager
